"use client";

import { useMemo } from "react";
import { useSpots } from "@/hooks/use-spots";
import { useZones } from "@/hooks/use-zones";
import { QuickAddSelect } from "./quick-add-select";
import type { Spot, Zone } from "@/lib/types";

function getDescendantZoneIds(zones: Zone[], zoneId: number): Set<number> {
  const children = new Map<number, number[]>();
  for (const zone of zones) {
    if (zone.parent_zone !== null) {
      const siblings = children.get(zone.parent_zone) ?? [];
      siblings.push(zone.id);
      children.set(zone.parent_zone, siblings);
    }
  }

  const ids = new Set<number>([zoneId]);
  const stack = [zoneId];
  while (stack.length > 0) {
    const current = stack.pop()!;
    for (const childId of children.get(current) ?? []) {
      if (!ids.has(childId)) {
        ids.add(childId);
        stack.push(childId);
      }
    }
  }
  return ids;
}

export interface SpotPickerProps {
  locationId: number;
  zoneId?: number | null;
  value: number | null;
  onChange: (spotId: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function SpotPicker({
  locationId,
  zoneId,
  value,
  onChange,
  placeholder,
  disabled,
  className,
}: SpotPickerProps) {
  const { data, isLoading } = useSpots(
    { location: locationId, page_size: 1000 },
    !!locationId
  );
  const { data: zonesData } = useZones(
    { location: locationId, page_size: 1000 },
    !!locationId
  );

  const zoneNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const z of zonesData?.results ?? []) {
      map.set(z.id, z.path);
    }
    return map;
  }, [zonesData]);

  const allowedZoneIds = useMemo(() => {
    const zones = zonesData?.results ?? [];
    if (!zoneId || zones.length === 0) return null;
    return getDescendantZoneIds(zones, zoneId);
  }, [zonesData, zoneId]);

  const options = useMemo(() => {
    const all = data?.results ?? [];
    if (!allowedZoneIds) return all;
    return all.filter(
      (s: Spot) => s.zone === null || allowedZoneIds.has(s.zone)
    );
  }, [data, allowedZoneIds]);

  const selected = useMemo(
    () => options.find((s: Spot) => s.id === value) ?? null,
    [options, value]
  );

  return (
    <QuickAddSelect
      value={selected}
      onChange={(spot) => onChange(spot?.id ?? null)}
      options={options}
      getOptionValue={(s: Spot) => s.id}
      getOptionLabel={(s: Spot) => s.name}
      getOptionDescription={(s: Spot) =>
        s.zone ? zoneNames.get(s.zone) ?? "" : ""
      }
      isLoading={isLoading || zonesData === undefined}
      placeholder={placeholder ?? "Select a spot"}
      disabled={disabled}
      className={className}
    />
  );
}
